import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { showToast } from "../toastService";
import { apiFetch } from "../utils/api";
import Header from "../components/Header";
import BottomNavbar from "../components/BottomNavbar";
import WorkCard from "../components/WorkCard";
import "./css/PastWorks.css";
import noDataImage from "../assets/images/no_data.png";

const USER_BASE_URL = process.env.REACT_APP_USER_BASE_URL;
const VERSION = process.env.REACT_APP_VERSION;
const LIMIT = 20;

export default function PastWorks() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [works, setWorks] = useState([]);
  const [loadingWorks, setLoadingWorks] = useState(false);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [lang, setLang] = useState(localStorage.getItem("app_language") || "en");
  const observerTarget = useRef(null);

  // Translations
  const t = {
    en: {
      generalSettings: "General Settings",
      pastWorks: "Past Works",
      noPastWorks: "Start posting your work to see them listed here.",
      loading: "Loading",
    },
    ar: {
      generalSettings: "الإعدادات العامة",
      pastWorks: "الأعمال السابقة",
      noPastWorks: "ابدأ بنشر أعمالك لتظهر هنا.",
      loading: "جاري التحميل",
    },
  }[lang];

  // Fetch profile on mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const data = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/profile/`,
          "GET",
          null,
          {}
        );

        if (data && data.response_code === 200 && data.response) {
          const profileData = data.response;
          
          // Set language preference
          if (profileData.language_preference) {
            localStorage.setItem("app_language", profileData.language_preference);
            setLang(profileData.language_preference);
          }

        }
      } catch (err) {
        console.error("Error fetching profile:", err);
        showToast(err.message || "Failed to load profile", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPastWorks = useCallback(
    async (page = 1) => {
      try {
        setLoadingWorks(true);
        const url = `${USER_BASE_URL}/${VERSION}/work/past/?limit=${LIMIT}&page=${page}`;
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "app-version": "1.0.0",
          },
        });
        const data = await response.json();

        if (data && data.response_code === 200) {
          if (page === 1) {
            setWorks(data.response || []);
          } else {
            setWorks((prev) => [...prev, ...(data.response || [])]);
          }
          setHasMorePages((data.response || []).length === LIMIT);
          setCurrentPage(page);
        }
      } catch (err) {
        console.error("Error fetching past works:", err);
        showToast(err.message || "Failed to load past works", "error");
      } finally {
        setLoadingWorks(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchPastWorks(1);
  }, [fetchPastWorks]);

  const handleLoadMore = useCallback(() => {
    if (!loadingWorks && hasMorePages) {
      fetchPastWorks(currentPage + 1);
    }
  }, [fetchPastWorks, loadingWorks, hasMorePages, currentPage]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMorePages && !loadingWorks) {
          handleLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [handleLoadMore, hasMorePages, loadingWorks]);

  // Loading screen
  if (loading) {
    return (
      <div className="past-works-loading">
        <div className="spinner"></div>
        <p>{t.loading}</p>
      </div>
    );
  }

  return (
    <div className="past-works-container" dir={lang === "ar" ? "rtl" : "ltr"}>
      {/* Header */}
      <Header lang={lang} />

      {/* Page Header with Back Button */}
      <div className="past-works-page-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <i className="bx bx-arrow-back"></i>
        </button>
        <h1 className="past-works-page-title">{t.generalSettings}</h1>
        <div className="header-spacer"></div>
      </div>

      {/* Main Content */}
      <div className="past-works-content">
        {/* Section Heading */}
        <h2 className="past-works-heading">{t.pastWorks}</h2>

        {/* Past Works List */}
        {loadingWorks && works.length === 0 ? (
          <div className="past-works-loading-works">
            <div className="spinner"></div>
            <p>{t.loading}</p>
          </div>
        ) : works.length === 0 ? (
          <div className="past-works-empty-container">
            <img
              src={noDataImage}
              alt="No Past Works"
              className="past-works-empty-image"
            />
            <p className="past-works-empty-text">{t.noPastWorks}</p>
          </div>
        ) : (
          <div className="past-works-list">
            {works.map((work) => (
              <WorkCard key={work._id} work={work} lang={lang} />
            ))}
            {hasMorePages && (
              <div ref={observerTarget} className="past-works-infinite-trigger">
                {loadingWorks && <div className="spinner small"></div>}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <BottomNavbar currentPage="find" lang={lang} />
    </div>
  );
}

